import React, { useState, useEffect } from 'react';
import { Smartphone, Globe, MapPin } from 'lucide-react';
import { Card, Badge } from '../components/UI';
import Pagination from '../components/Pagination';
import { deviceAPI } from '../api/apiClient';
import toast from 'react-hot-toast';

/**
 * Device Manager Page
 */
const DeviceManagerPage = () => {
  const DEVICES_PER_PAGE = 50;
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterActive, setFilterActive] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadDevices();
    loadStats();
  }, [page, filterActive]);

  const loadDevices = async () => {
    try {
      setLoading(true);
      const response = await deviceAPI.getAll(
        page,
        DEVICES_PER_PAGE,
        filterActive,
        ''
      );
      setDevices(response.data.data.devices);
      setTotalPages(response.data.data.pagination.pages);
    } catch (error) {
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await deviceAPI.getStats();
      setStats(response.data.data);
    } catch (error) {
      toast.error('Failed to load device stats');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString() + ' ' +
           new Date(date).toLocaleTimeString();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
          <Smartphone size={32} className="text-purple-400" />
          <span>Device Manager</span>
        </h1>
        <p className="text-gray-400 mt-2">Track and manage connected devices</p>
      </div>

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <h3 className="text-gray-400 text-sm mb-2">Total Devices</h3>
            <p className="text-3xl font-bold text-white">{stats.totalDevices}</p>
          </Card>

          <Card>
            <h3 className="text-gray-400 text-sm mb-2">Active Devices</h3>
            <p className="text-3xl font-bold text-green-400">
              {stats.activeDevices}
            </p>
          </Card>

          <Card>
            <h3 className="text-gray-400 text-sm mb-2">Top Country</h3>
            <p className="text-3xl font-bold text-purple-400">
              {stats.devicesByCountry && stats.devicesByCountry.length > 0
                ? stats.devicesByCountry[0]._id
                : 'N/A'}
            </p>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <div className="flex space-x-4">
          <button
            onClick={() => {
              setFilterActive('');
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filterActive === ''
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            All
          </button>
          <button
            onClick={() => {
              setFilterActive('true');
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filterActive === 'true'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => {
              setFilterActive('false');
              setPage(1);
            }}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filterActive === 'false'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            Inactive
          </button>
        </div>
      </Card>

      {/* Devices Table */}
      <Card>
        {loading ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : devices.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No devices found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    Device
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    OS
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    Location
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    Status
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    Last Active
                  </th>
                  <th className="text-left px-6 py-3 text-gray-300 font-semibold">
                    Watch Time
                  </th>
                </tr>
              </thead>
              <tbody>
                {devices.map((device) => (
                  <tr
                    key={device._id}
                    className="border-b border-gray-700 hover:bg-gray-700 bg-opacity-50 transition"
                  >
                    <td className="px-6 py-3">
                      <div>
                        <p className="text-white font-medium">{device.deviceModel}</p>
                        <p className="text-gray-400 text-sm">{device.deviceId}</p>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <div>
                        <p className="text-white">{device.osType}</p>
                        <p className="text-gray-400 text-sm">{device.osVersion}</p>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center space-x-2 text-white">
                        <MapPin size={16} />
                        <span>{device.country}</span>
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <Badge
                        variant={device.isActive ? 'success' : 'danger'}
                      >
                        {device.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-6 py-3 text-gray-400 text-sm">
                      {formatDate(device.lastActive)}
                    </td>
                    <td className="px-6 py-3 text-gray-400">
                      {Math.floor(device.totalWatchTime / 3600)}h{' '}
                      {Math.floor((device.totalWatchTime % 3600) / 60)}m
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(newPage) => {
            if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
          }}
        />
      </Card>

      {/* OS Distribution */}
      {stats && stats.devicesByOS && stats.devicesByOS.length > 0 && (
        <Card>
          <h3 className="text-lg font-bold text-white mb-4">OS Distribution</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stats.devicesByOS.map((osStats, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
              >
                <span className="text-white font-medium">{osStats._id}</span>
                <span className="text-purple-400 font-bold">{osStats.count} devices</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default DeviceManagerPage;
